// 获取树形结构版本下拉的选项
import React from 'react';
import { groupBy } from "lodash";
import { Tag } from 'antd';
import { VersionUtil } from 'utils/version-util';
import {ze} from 'utils/zhEn';

const VERSION_SEPARATOR = '.';
const WILDCARD_CHARACTER = VersionUtil.WILDCARD_CHARACTER;
// 脏版本合并到"其他"之后，这个"其他"父节点的value占位符
const DIRTY_DATA_PARENT_VALUE_PLACEHOLDER = VersionUtil.DIRTY_DATA_PARENT_VALUE_PLACEHOLDER;

/**
 * 把传入的 option 上的 versionAlias 规范化：null / undefined / 全空白 都视为「无别名」，统一返回 ''。
 * @param {{versionAlias?: string}} [option]
 * @returns {string}
 */
function normalizeAlias(option) {
  const raw = option && option.versionAlias;
  if (raw === null || raw === undefined) {
    return '';
  }
  return String(raw).trim();
}

/**
 * 渲染叶子节点的 title：版本号 + 别名 Tag（如果有）。
 * 父节点 / 通配符节点不调用本函数，保持纯字符串 title。
 * 选中后回显的 chip 也会用这个 JSX（antd TreeSelect 默认 treeNodeLabelProp='title'）。
 *
 * 导出给调用方复用，方便在 getTreeSelectVersionOptions 生成的树之上做「重新装饰展示文案」之类的
 * 二次处理时，也能保持叶子节点别名 Tag 的渲染效果不变（比如 summaryReducer.js 的
 * decorateVersionTreeWithDecodedLabels）。
 */
export function renderLeafTitle(versionValue, alias) {
  if (!alias) {
    return versionValue;
  }
  // 用 inline-flex + align-items:center 确保 Tag 与版本号文字垂直居中对齐：
  // 直接用 verticalAlign:'middle' 在 antd TreeSelect 节点内会受父容器 line-height / 字号影响，
  // 表现不稳定（看起来会偏下）。flex 布局是最稳的方案。
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      {versionValue}
      <Tag color="geekblue" style={{ marginRight: 0 }}>
        {alias}
      </Tag>
    </span>
  );
}

/**
 * 将一个项目版本号的options数组，转换成树形结构，提供给antd TreeSelect组件使用。实现的效果是按照版本号的点号分节，将版本号组织成树形结构。
 *
 * 如果 option 上带有 `versionAlias` 字段（非空且 trim 后非空），叶子节点会：
 *   1) `title` 渲染成 `版本号 + <Tag>别名</Tag>` 的 JSX（同时影响下拉项与选中回显）
 *   2) 节点上挂一个 `versionAlias` 自定义属性，用于 VersionHybridSelect 的别名搜索匹配
 *
 * @param {{ value: string, millisSinceUpdate?: number, versionAlias?: string }[]}versionList
 * @returns {*}
 */
export function getTreeSelectVersionOptions(versionList) {
  /**
   * @param {{value: string, label: string, splitSections: string[]}[]} optionList
   */
  function recursiveRoutine(optionList, childIndex = 0) {
    const groupByDict = groupBy(optionList, (x) => {
      if (childIndex < x.splitSections.length - 1) {
        return JSON.stringify([
          true,
          x.splitSections.slice(0, childIndex + 1).join(VERSION_SEPARATOR) + VERSION_SEPARATOR + WILDCARD_CHARACTER,
          x.splitSections[childIndex],
        ]);
      }
      return JSON.stringify([
        false,
        x.splitSections.slice(0, childIndex + 1).join(VERSION_SEPARATOR),
        x.splitSections[childIndex],
      ]);
    });

    return Object.entries(groupByDict)
      .map(([k, v]) => {
        const [
          hasChildren, // 是否有子节点（子版本）
          currentNodeValue, // 当前节点的版本号（父版本的话带通配符）
          currentSegmentValue, // 当前节点的版本号对应当前小节的值，用来排序
        ] = JSON.parse(k);
        // 仅叶子节点考虑别名（按需求 3：父节点 / 通配符节点不展示别名）
        const leafAlias = !hasChildren ? normalizeAlias(v[0]) : '';
        return {
          title: hasChildren ? currentNodeValue : renderLeafTitle(currentNodeValue, leafAlias),
          value: currentNodeValue,
          key: currentNodeValue,
          currentSegmentValue,
          // 透传别名到节点上，供 VersionHybridSelect 做搜索匹配 / 父节点搜索过滤选择
          ...(leafAlias ? { versionAlias: leafAlias } : {}),
          ...(hasChildren && { children: recursiveRoutine(v, childIndex + 1) }),
        };
      })
      .sort((a, b) => VersionUtil.compareVersionSegment(b.currentSegmentValue, a.currentSegmentValue));
  }

  const optionList = versionList.map(x => ({
    ...x,
    splitSections: x.value.split(VERSION_SEPARATOR),
  }));

  // option里面是否带有更新时间
  const hasAnyMillisSinceUpdate = optionList.some(x => x.millisSinceUpdate);
  // 是否需要自动把脏数据版本合并到"其他"里面去
  const needAutoMergeDirtyData = hasAnyMillisSinceUpdate;

  const dirtyMajorVersionSet = needAutoMergeDirtyData ? getDirtyMajorVersionSet(optionList) : new Set();

  const treeSelectVersionOptions = recursiveRoutine(optionList);

  /**
   * 精简树形结构，如果一个父节点只有1个子节点，就用这个子节点取代掉父节点
   */
  function shrinkTree(treeOptions) {
    return treeOptions.map((x) => {
      while (x.children && x.children.length === 1) {
        x = x.children[0];
      }
      if (x.children) {
        return {
          ...x,
          children: shrinkTree(x.children),
        };
      }
      return x;
    });
  }

  /**
   * 把脏版本的整棵树移到"其他"顶层节点下面
   * @param {Array} treeOptions 树形结构的版本选项
   * @param {Set<string>} dirtyMajorVersionSet 脏数据大版本号集合
   * @returns {Array} 处理后的树形结构
   */
  function mergeDirtyVersionsToOther(treeOptions, dirtyMajorVersionSet) {
    if (dirtyMajorVersionSet.size === 0) {
      return treeOptions;
    }

    const cleanVersions = [];
    const dirtyVersions = [];

    for (const node of treeOptions) {
      // 获取这个节点的大版本号（value可能带通配符，需要取第一个segment）
      const majorVersion = node.value.split(VERSION_SEPARATOR)[0];
      if (dirtyMajorVersionSet.has(majorVersion)) {
        dirtyVersions.push(node);
      } else {
        cleanVersions.push(node);
      }
    }

    if (dirtyVersions.length === 0) {
      return treeOptions;
    }

    // 创建"其他"顶层节点，把脏版本树作为其子节点
    const otherNode = {
      title: ze('其他', 'Others'),
      value: DIRTY_DATA_PARENT_VALUE_PLACEHOLDER,
      key: DIRTY_DATA_PARENT_VALUE_PLACEHOLDER,
      children: dirtyVersions,
      disabled: true,
    };

    return [...cleanVersions, otherNode];
  }

  const shrinkedTree = shrinkTree(treeSelectVersionOptions);

  if (needAutoMergeDirtyData) {
    return mergeDirtyVersionsToOther(shrinkedTree, dirtyMajorVersionSet);
  }
  return shrinkedTree;
}

/**
 * 通用的树形选项遍历/改写函数：对 treeOptions 里的每一个节点（根节点、中间节点、叶子节点，深度优先）
 * 都调用一次 visit 回调。
 * - visit 回调返回一个新的节点对象，则用它替换原节点（children 会在外层重新递归处理，回调不需要关心）；
 * - visit 回调返回 falsy 值（包括不写 return），则保留原节点不变。
 * 纯函数，不会修改传入的 treeOptions，返回一棵新树。
 *
 * @param {Array} treeOptions getTreeSelectVersionOptions 生成的树（或其子树）
 * @param {(node: object) => (object|void)} visit
 * @returns {Array}
 */
export function mapTreeSelectOptions(treeOptions, visit) {
  return (treeOptions || []).map((node) => {
    const nextNode = visit(node) || node;
    if (nextNode.children) {
      return { ...nextNode, children: mapTreeSelectOptions(nextNode.children, visit) };
    }
    return nextNode;
  });
}

/**
 *
 * @param treeSelectValue
 * @param {{ value: String }[]} versionList
 * @returns {string[]} 版本的value列表
 */
export function getVersionsFromTreeSelectValue(treeSelectValue, versionList) {
  return treeSelectValue.map((x) => {
    if (x.endsWith(WILDCARD_CHARACTER)) {
      const prefix = x.substring(0, x.length - WILDCARD_CHARACTER.length);
      return versionList.filter(y => y.value && y.value.startsWith(prefix)).map(z => z.value);
    }
    return [x];
  }).flat();
}

// ==================== 脏版本判定相关 ====================

// 时间阈值常量
const TWO_DAYS_MS = 2 * 24 * 60 * 60 * 1000;    // 2天的毫秒数
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;  // 7天的毫秒数
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000; // 30天的毫秒数

// 分数常量
const SCORE_RECENT_2_DAYS = 5;   // 最近2天内的版本得分
const SCORE_RECENT_7_DAYS = 2;   // 最近7天内的版本得分
const SCORE_RECENT_30_DAYS = 1;  // 最近30天内的版本得分
const SCORE_OTHER = 0.2;         // 其他版本得分
const SCORE_THRESHOLD_ACTIVE = 10;  // 活跃大版本的分数阈值
const SCORE_THRESHOLD_DIRTY = 5;    // 脏版本判定的分数阈值
const TOP_N_COUNT = 3;              // 用于判断正常模式的 top N 数量
const MIN_VERSION_COUNT = 10;       // 版本数量少于此值时不做隐藏处理

/**
 * 获取脏数据大版本号集合。
 *
 * ## 判定方案
 *
 * ### 前置条件
 * 1. 如果版本号总数 < 10个，不做任何隐藏处理，返回空集合
 *
 * ### 步骤1：计算每个大版本的总分
 * - 遍历所有版本号，根据 millisSinceUpdate 加分：
 *   - 最近2天内：+5分
 *   - 最近7天内：+2分
 *   - 最近30天内：+1分
 *   - 其他：+0.2分
 * - 将同一大版本下所有子版本的分数累加，作为该大版本的总分
 *
 * ### 步骤2：检查是否需要继续处理
 * - 判断是否存在总分 ≥ 10分 的大版本
 * - 如果不存在，则不做任何隐藏处理，返回空集合
 *
 * ### 步骤3：判断项目的"正常版本号模式"
 * - 从总分 ≥ 10分 的大版本里，选取总分最高的 top N 个（N = min(3, 符合条件的大版本数量)）
 * - 将这些 top N 大版本号各自去掉数字部分（删除所有数字字符），得到"模式字符串"
 *   - 例："1" → ""，"v1" → "v"，"release-2" → "release-"
 * - 如果所有 top N 的模式字符串完全相同（包括都是空字符串的情况），则认定这个模式为项目的"正常版本号模式"，继续步骤4
 * - 如果 top N 的模式字符串不完全相同，则跳过步骤4，直接进入步骤5
 *
 * ### 步骤4：根据模式过滤分数（仅当步骤3确定了正常模式时执行）
 * - 遍历所有大版本，将大版本号去掉数字部分，得到其模式字符串
 * - 如果该模式字符串与"正常版本号模式"不同，则将该大版本的分数设置为0
 *
 * ### 步骤5：判定脏版本
 * - 将所有得分 < 5 的大版本判定为脏版本，加入脏版本集合
 *
 * @param {{splitSections: string[], millisSinceUpdate?: number, value: string}[]} optionList
 * @returns {Set<string>} 脏数据大版本号集合
 */
function getDirtyMajorVersionSet(optionList) {
  // 前置条件：版本数量少于阈值时不做隐藏处理
  if (optionList.length < MIN_VERSION_COUNT) {
    return new Set();
  }

  /**
   * 根据版本的最近更新时间计算单个版本的得分
   * @param {number} millisSinceUpdate 距离上次更新的毫秒数
   * @returns {number} 得分
   */
  function getVersionScore(millisSinceUpdate) {
    if (millisSinceUpdate <= TWO_DAYS_MS) {
      return SCORE_RECENT_2_DAYS;
    }
    if (millisSinceUpdate <= SEVEN_DAYS_MS) {
      return SCORE_RECENT_7_DAYS;
    }
    if (millisSinceUpdate <= THIRTY_DAYS_MS) {
      return SCORE_RECENT_30_DAYS;
    }
    return SCORE_OTHER;
  }

  /**
   * 将大版本号去掉数字部分，得到模式字符串
   * 例："1" → ""，"v1" → "v"，"release-2" → "release-"
   * @param {string} majorVersion 大版本号
   * @returns {string} 模式字符串
   */
  function getPatternString(majorVersion) {
    return majorVersion.replace(/\d/g, '');
  }

  // ========== 步骤1：计算每个大版本的总分 ==========
  // majorVersionScoreMap: { 大版本号 -> 总分 }
  const majorVersionScoreMap = new Map();

  for (const option of optionList) {
    const majorVersion = option.splitSections[0];
    const score = getVersionScore(option.millisSinceUpdate);
    const currentScore = majorVersionScoreMap.get(majorVersion) || 0;
    majorVersionScoreMap.set(majorVersion, currentScore + score);
  }

  // ========== 步骤2：检查是否存在活跃大版本 ==========
  // 筛选出总分 >= SCORE_THRESHOLD_ACTIVE 的大版本
  const activeMajorVersions = [...majorVersionScoreMap.entries()]
    .filter(([, score]) => score >= SCORE_THRESHOLD_ACTIVE);

  // 如果不存在活跃大版本，不做任何隐藏处理
  if (activeMajorVersions.length === 0) {
    return new Set();
  }

  // ========== 步骤3：判断项目的"正常版本号模式" ==========
  // 按分数降序排序，取 top N
  const topNMajorVersions = activeMajorVersions
    .sort((a, b) => b[1] - a[1])
    .slice(0, TOP_N_COUNT)
    .map(([majorVersion]) => majorVersion);

  // 获取 top N 的模式字符串
  const topNPatterns = topNMajorVersions.map(getPatternString);

  // 检查 top N 的模式是否完全相同
  const firstPattern = topNPatterns[0];
  const allPatternsMatch = topNPatterns.every(pattern => pattern === firstPattern);

  // ========== 步骤4：根据模式过滤分数（仅当模式一致时执行） ==========
  if (allPatternsMatch) {
    const normalPattern = firstPattern;
    // 遍历所有大版本，如果模式不匹配，则分数设为0
    for (const [majorVersion] of majorVersionScoreMap) {
      if (getPatternString(majorVersion) !== normalPattern) {
        majorVersionScoreMap.set(majorVersion, 0);
      }
    }
  }

  // ========== 步骤5：判定脏版本 ==========
  const dirtyMajorVersions = new Set();
  for (const [majorVersion, score] of majorVersionScoreMap) {
    if (score < SCORE_THRESHOLD_DIRTY) {
      dirtyMajorVersions.add(majorVersion);
    }
  }

  return dirtyMajorVersions;
}
